import { useId, useState } from "react";
import type { CreditPack } from "@/api/billing";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatCredits, formatPrice } from "./format";

type PackGridProps = {
	packs: CreditPack[];
	pendingPackId: string | null;
	disabled?: boolean;
	disabledReason?: string;
	className?: string;
	onBuy: (pack: CreditPack, saveCard: boolean) => void;
};

/** Pay-as-you-go packs. Pack credits never expire and are spent after plan credits. */
export function PackGrid({
	packs,
	pendingPackId,
	disabled,
	disabledReason,
	className,
	onBuy,
}: PackGridProps) {
	const [saveCard, setSaveCard] = useState(false);
	const checkboxId = useId();
	const base = packs[0] ? packs[0].priceCents / (packs[0].millicredits / 1000) : 0;
	return (
		<div className={cn("flex flex-col gap-3", className)}>
			<div className="grid gap-3 sm:grid-cols-3">
				{packs.map((pack) => {
					const perCredit = pack.priceCents / (pack.millicredits / 1000);
					const saving = base > 0 ? Math.round((1 - perCredit / base) * 100) : 0;
					return (
						<div key={pack.id} className="flex flex-col gap-3 rounded-lg border bg-card p-4">
							<div className="flex items-center justify-between">
								<span className="mono-label">Pack</span>
								{saving > 0 && <span className="mono-label text-foreground">Save {saving}%</span>}
							</div>
							<div className="flex flex-col">
								<span className="font-mono text-xl tabular-nums">
									{formatCredits(pack.millicredits)}
								</span>
								<span className="text-[12px] text-muted-foreground">credits · never expire</span>
							</div>
							<div className="flex items-baseline justify-between">
								<span className="text-lg font-medium tabular-nums">
									{formatPrice(pack.priceCents)}
								</span>
								<span className="font-mono text-[11px] text-muted-foreground">
									{(perCredit / 100).toLocaleString(undefined, {
										style: "currency",
										currency: "USD",
										minimumFractionDigits: 3,
									})}
									/cr
								</span>
							</div>
							<Button
								size="sm"
								variant="outline"
								disabled={disabled || (pendingPackId !== null && pendingPackId !== pack.id)}
								loading={pendingPackId === pack.id}
								title={disabled ? disabledReason : undefined}
								onClick={() => onBuy(pack, saveCard)}
							>
								Buy {formatCredits(pack.millicredits)}
							</Button>
						</div>
					);
				})}
			</div>
			<label
				htmlFor={checkboxId}
				className="flex items-center gap-2 text-[12.5px] text-muted-foreground"
			>
				<input
					id={checkboxId}
					type="checkbox"
					className="size-3.5 accent-foreground"
					checked={saveCard}
					onChange={(event) => setSaveCard(event.target.checked)}
					disabled={disabled}
				/>
				Use this card for auto top-up
			</label>
		</div>
	);
}
